import React, { useEffect, useState } from "react";
import { Typography, Container, Skeleton, Alert, Box } from "@mui/material";
import Header from "../../components/Header/Header";
import Footer from "../../components/Footer/Footer";
import { db } from "../../firestore-DB-config/firebase-config"; // Ensure Firestore is initialized
import { collection, doc, getDoc } from "firebase/firestore";
import { useLoader } from "../../components/loader/loader-context";

// Define types for the sections
interface Section {
  heading: string;
  paragraph: string;
}

const PrivacyPolicy: React.FC = () => {
  const [sections, setSections] = useState<Section[]>([]);
  const [error, setError] = useState<string | null>(null); // Error state
  const { showLoader, hideLoader } = useLoader();
  const [loading, setLoading] = useState(true); // Loading state

  useEffect(() => {
    // Fetch existing privacy policy from Firestore
    const fetchPrivacyPolicy = async () => {
      showLoader();
      try {
        const docRef = doc(collection(db, "privacyPolicy"), "latest");
        const docSnap = await getDoc(docRef);

        if (docSnap.exists()) {
          const data = docSnap.data();
          if (data.policy) {
            setSections(data.policy); // Set the existing privacy policy
          }
        }
      } catch (error) {
        setError(
          "Failed to load Privacy Policy. Please try again later."
        );
        console.error("Error fetching privacy policy:", error);
      } finally {
        hideLoader();
        setLoading(false); // End loading
      }
    };

    fetchPrivacyPolicy();
  }, []);

  return (
    <>
      <Header />
      <Container sx={{ my: { xs: 6, md: 8 }, px: { xs: 2.5, sm: 2, lg: 0 } }}>
        <Typography
          fontSize="32px"
          fontWeight={600}
          color="#011D33"
          gutterBottom
        >
          Privacy Policy
        </Typography>
        {loading ? (
          // Skeleton loaders while fetching data
          <>
            {[1, 2, 3].map((i) => (
              <Box key={i} sx={{ mb: 3 }}>
                <Skeleton variant="text" width="60%" height={40} />
                <Skeleton variant="text" width="90%" height={30} />
                <Skeleton variant="text" width="90%" height={30} />
              </Box>
            ))}
          </>
        ) : error ? (
          // Display error if fetching fails
          <Alert severity="error" sx={{ mt: 4 }}>
            {error}
          </Alert>
        ) : (
          // Render fetched sections
          sections.map((section, index) => (
            <div key={index}>
              <Typography
                sx={{ fontSize: { xs: "18px", sm: "20px" } }}
                fontWeight={650}
                color="#011D33"
                gutterBottom
              >
                {section.heading}
              </Typography>
              <Typography
                color="textSecondary"
                sx={{ fontSize: { xs: "14px", sm: "17px" } }}
                fontWeight={300}
              >
                <div dangerouslySetInnerHTML={{ __html: section.paragraph }} />
              </Typography>
            </div>
          ))
        )}
      </Container>
      <Footer />
    </>
  );
};

export default PrivacyPolicy;
